import { LaunchContextConfig, LaunchContextSelection, LaunchContextResult } from './types';

// Booleans/numbers must reach the SDK with their native type — the strict
// matchers treat "true" and true as distinct values (NAM-1131).
function toAttributeValue(value: unknown): string | boolean | number {
  return typeof value === 'boolean' || typeof value === 'number' ? value : String(value);
}

export function buildLaunchContext(
  config: LaunchContextConfig,
  selection: LaunchContextSelection
): LaunchContextResult {
  const result: LaunchContextResult = { customAttributes: {} };

  if (selection.productGroupsEnabled && selection.selectedProductGroupIds.size > 0) {
    result.productGroups = Array.from(selection.selectedProductGroupIds);
  }

  if (selection.currentGroupEnabled && selection.selectedCurrentGroupId) {
    result.currentGroup = selection.selectedCurrentGroupId;
  }

  if (selection.customObjectEnabled && selection.selectedCustomObjectId && config.customObject) {
    const choice = config.customObject.choices.find(c => c.id === selection.selectedCustomObjectId);
    if (choice) {
      result.customObject = choice.value;
    }
  }

  if (selection.customAttributesEnabled) {
    if (selection.appliedPresetId && config.customAttributes?.presets) {
      const preset = config.customAttributes.presets.find(p => p.id === selection.appliedPresetId);
      if (preset) {
        for (const [key, value] of Object.entries(preset.values)) {
          result.customAttributes[key] = toAttributeValue(value);
        }
      }
    }
    for (const [key, value] of Object.entries(selection.attributeValues)) {
      if (value !== null && value !== undefined && value !== '') {
        result.customAttributes[key] = toAttributeValue(value);
      }
    }
  }

  return result;
}

export function createDefaultSelection(config: LaunchContextConfig): LaunchContextSelection {
  return {
    productGroupsEnabled: config.productGroups?.enabledByDefault ?? false,
    selectedProductGroupIds: new Set(config.productGroups?.defaultSelection ?? []),
    currentGroupEnabled: config.currentGroup?.enabledByDefault ?? false,
    selectedCurrentGroupId: config.currentGroup?.defaultSelection ?? undefined,
    customObjectEnabled: config.customObject?.enabledByDefault ?? false,
    selectedCustomObjectId: config.customObject?.defaultSelection,
    customAttributesEnabled: config.customAttributes?.enabledByDefault ?? false,
    attributeValues: buildDefaultAttributeValues(config),
    appliedPresetId: undefined,
    currentTag: config.defaultTag ?? 'all',
  };
}

function buildDefaultAttributeValues(config: LaunchContextConfig): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  if (config.customAttributes?.attributes) {
    for (const attr of config.customAttributes.attributes) {
      // Only pre-populate (and thus render ON) attributes explicitly marked
      // enabledByDefault. defaultValue is still used as the value applied when a
      // user toggles the attribute on, and as an enum's initial selection.
      if (attr.enabledByDefault && attr.defaultValue !== undefined) {
        values[attr.key] = attr.defaultValue;
      }
    }
  }
  return values;
}
